import { useEffect, useRef, useState } from "react";
import { LuArrowRightLeft, LuBanknote, LuCheck, LuCoins, LuDices, LuGavel, LuHammer, LuHouse, LuKeyRound, LuLandmark, LuLock, LuLockOpen, LuTicket, LuX } from "react-icons/lu";
import { Avatar } from "../../components/Avatar";
import { GROUPS, SQUARES, priceOf, type Group, type MonopolyView } from "../../../shared/games/monopoly";
import { GROUP_COLOR, SEAT_COLOR, fieldKind, fieldText, groupHeld, kr, mortgageValue, rentRows, unmortgageCost } from "./common";

/** a number that counts up or down to its new value (but waits while the board is still playing the move that caused it) */
export function useCountUp(target: number, hold: boolean): number {
  const [shown, setShown] = useState(target);
  const cur = useRef(target);
  const raf = useRef(0);
  useEffect(() => {
    if (hold || cur.current === target) return;
    const calm = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (calm) { cur.current = target; setShown(target); return; }
    const from = cur.current, t0 = performance.now(), dur = Math.min(900, 280 + Math.abs(target - from) * 1.1);
    const step = (now: number) => {
      const u = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - u, 3);
      cur.current = Math.round(from + (target - from) * e);
      setShown(cur.current);
      if (u < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, hold]);
  return shown;
}

/** true while `active` has lasted less than `maxMs` – a safety net so that nothing in the UI can wait for an animation for ever */
export function useHold(active: boolean, maxMs: number): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!active) { setExpired(false); return; }
    const id = setTimeout(() => setExpired(true), maxMs);
    return () => clearTimeout(id);
  }, [active, maxMs]);
  return active && !expired;
}

export function useMedia(query: string, initial = false): boolean {
  const [m, setM] = useState(() => (typeof matchMedia === "function" ? matchMedia(query).matches : initial));
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(query);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return m;
}

/* ───────────────────────── player cards ───────────────────────── */

export function PlayerChip({ b, p, seat, you, names, held, open, onToggle, onTile, compact }: {
  b: MonopolyView; p: any; seat: number; you: number; names: (s: number) => string; held: boolean; open: boolean; onToggle: () => void; onTile: (i: number) => void; compact?: boolean;
}) {
  const pl = b.players[seat]!;
  const cash = useCountUp(pl.cash, held);
  const turn = b.actor === seat && !pl.out && !b.winner;
  const owned = b.owner.map((o, i) => (o === seat ? i : -1)).filter((i) => i >= 0);
  return (
    <div className={`m3-chip ${turn ? "turn" : ""} ${pl.out ? "out" : ""} ${seat === you ? "me" : ""} ${compact ? "compact" : ""}`} style={{ "--seat": SEAT_COLOR[seat] }}>
      <button type="button" className="m3-chip-main" onClick={onToggle} aria-expanded={open} aria-label={`${names(seat)}${seat === you ? " (dig)" : ""}: ${kr(pl.cash)}${pl.out ? ", konkurs" : ""}. Vis ejendomme.`}>
        <span className="m3-ava"><Avatar equipped={p?.avatar} size={compact ? 30 : 44} label="" framing="bust" /></span>
        <span className="m3-chip-text">
          <b className="m3-name">{names(seat)}{seat === you ? " · dig" : ""}</b>
          <span className="m3-cash" title={`Samlet formue ${kr(pl.worth)}`}><LuCoins size={14} aria-hidden="true" /> {cash.toLocaleString("da-DK")}</span>
        </span>
        <span className="m3-badges">
          {pl.jail > 0 && !pl.out && <span className="m3-badge jail" title="I fængsel"><LuLock size={13} aria-label="I fængsel" role="img" /></span>}
          {pl.jailCards > 0 && <span className="m3-badge card" title={`${pl.jailCards} løsladelseskort`}><LuTicket size={13} aria-label="Løsladelseskort" role="img" />{pl.jailCards > 1 ? pl.jailCards : ""}</span>}
          {pl.out && <span className="m3-badge out">Konkurs</span>}
        </span>
      </button>
      {open && (
        <div className="m3-owned" role="group" aria-label={`${names(seat)}s ejendomme`}>
          {owned.length === 0 && <small>Ingen ejendomme endnu</small>}
          {owned.map((i) => {
            const q = SQUARES[i]!;
            const col = q.t === "prop" ? GROUP_COLOR[q.group] : q.t === "rail" ? "#475569" : "#94a3b8";
            const h = b.houses[i]!;
            return (
              <button key={i} type="button" className={`m3-dot ${b.mort[i] ? "mort" : ""}`} style={{ background: col }} onClick={() => onTile(i)}
                aria-label={`${q.name}${b.mort[i] ? ", pantsat" : ""}${h ? (h === 5 ? ", hotel" : `, ${h} huse`) : ""}`} title={`${q.name}${b.mort[i] ? " (pantsat)" : ""}`}>
                {h > 0 ? (h === 5 ? "H" : h) : ""}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── deed card ───────────────────────── */

export function DeedCard({ b, i, names, you, canManage, busy, go, onClose }: {
  b: MonopolyView; i: number; names: (s: number) => string; you: number; canManage: boolean; busy: boolean; go: (a: any) => void; onClose?: () => void;
}) {
  const q = SQUARES[i]!;
  const owner = b.owner[i];
  const buyable = q.t === "prop" || q.t === "rail" || q.t === "util";
  const mine = owner === you;
  const rows = rentRows(b, i);
  const held = groupHeld(b, i);
  const mgmt = b.options;
  const h = b.houses[i] ?? 0;
  const head = q.t === "prop" ? GROUP_COLOR[q.group] : q.t === "rail" ? "#334155" : q.t === "util" ? "#0e7490" : q.t === "chance" ? "#ff8a1f" : q.t === "chest" ? "#2f7bff" : q.t === "tax" ? "#7c3aed" : q.t === "go" ? "#16a34a" : q.t === "jail" ? "#ea580c" : q.t === "gojail" ? "#1e3a8a" : "#2563eb";
  const light = q.t === "prop" && ["yellow", "lblue", "pink"].includes(q.group);
  return (
    <article className={`m3-deed ${b.mort[i] ? "mort" : ""}`} aria-label={`Felt: ${q.name}`}>
      <header style={{ background: head, color: light ? "#14246a" : "#fff" }}>
        <small>{q.t === "prop" ? `${groupName(q.group)} · ${fieldKind(q)}` : fieldKind(q)}</small>
        <h3>{q.name}</h3>
        {onClose && <button type="button" className="m3-x" onClick={onClose} aria-label="Luk" style={{ color: light ? "#14246a" : "#fff" }}><LuX size={16} aria-hidden="true" /></button>}
      </header>
      <div className="m3-deed-body">
        {buyable ? (
          <>
            <div className="m3-deed-row"><span>Pris</span><b>{kr(priceOf(i))}</b></div>
            <div className="m3-deed-row"><span>Ejer</span><b style={owner !== null && owner !== undefined ? { color: SEAT_COLOR[owner] } : undefined}>{owner === null || owner === undefined ? "Ledig" : `${names(owner)}${mine ? " (dig)" : ""}`}</b></div>
            {b.mort[i] && <div className="m3-deed-flag"><LuLock size={14} aria-hidden="true" /> Pantsat – ingen leje</div>}
            {held && <div className="m3-deed-row"><span>Farvesæt</span><b>{held.have} af {held.of}</b></div>}
            <table className="m3-rent" aria-label="Lejetabel">
              <tbody>
                {rows.map((r) => <tr key={r.label} className={r.on ? "on" : ""}><td>{r.label}</td><td>{r.value}</td></tr>)}
              </tbody>
            </table>
            {q.t === "prop" && <div className="m3-deed-row"><span>Hus / hotel koster</span><b>{kr(q.house)}</b></div>}
            <div className="m3-deed-row"><span>Pantsætning giver</span><b>{kr(mortgageValue(i))}</b></div>
            {q.t === "prop" && h > 0 && <div className="m3-deed-row"><span>Bygninger</span><b>{h === 5 ? "Hotel" : `${h} ${h === 1 ? "hus" : "huse"}`}</b></div>}
          </>
        ) : (
          <p className="m3-deed-text">{fieldText(q)}</p>
        )}
        {mine && canManage && (
          <div className="m3-deed-actions">
            {q.t === "prop" && mgmt.build.includes(i) && <button className="btn small lime" disabled={busy} onClick={() => go({ type: "build", sq: i })}><LuHammer size={15} aria-hidden="true" /> Byg {h === 4 ? "hotel" : "hus"} · {kr(q.house)}</button>}
            {mgmt.sell.includes(i) && q.t === "prop" && <button className="btn small ghost" disabled={busy} onClick={() => go({ type: "sell", sq: i })}><LuHouse size={15} aria-hidden="true" /> Sælg bygning · +{kr(Math.floor(q.house / 2))}</button>}
            {mgmt.mortgage.includes(i) && <button className="btn small ghost" disabled={busy} onClick={() => go({ type: "mortgage", sq: i })}><LuBanknote size={15} aria-hidden="true" /> Pantsæt · +{kr(mortgageValue(i))}</button>}
            {mgmt.unmortgage.includes(i) && <button className="btn small ghost" disabled={busy} onClick={() => go({ type: "unmortgage", sq: i })}><LuLockOpen size={15} aria-hidden="true" /> Indløs · −{kr(unmortgageCost(i))}</button>}
            {q.t === "prop" && !mgmt.build.includes(i) && !b.mort[i] && h < 5 && <small className="muted">Du kan bygge, når du ejer hele farven, og husene bygges jævnt.</small>}
          </div>
        )}
      </div>
    </article>
  );
}
const groupName = (g: Group) => ({ brown: "Brun", lblue: "Lyseblå", pink: "Pink", orange: "Orange", red: "Rød", yellow: "Gul", green: "Grøn", dblue: "Mørkeblå" })[g];

/* ───────────────────────── my properties ───────────────────────── */

export function PropertyList({ b, you, canManage, busy, go, selected, onSelect }: { b: MonopolyView; you: number; canManage: boolean; busy: boolean; go: (a: any) => void; selected: number | null; onSelect: (i: number) => void }) {
  const mine = b.owner.map((o, i) => (o === you ? i : -1)).filter((i) => i >= 0);
  const mgmt = b.options;
  if (mine.length === 0) return <p className="muted" style={{ margin: 0 }}>Du ejer ingen ejendomme endnu. Land på en ledig gade og svar rigtigt for at købe den.</p>;
  const groups: { key: string; label: string; color: string; idx: number[]; total: number }[] = [];
  for (const i of mine) {
    const q = SQUARES[i]!;
    const key = q.t === "prop" ? q.group : q.t;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = q.t === "prop" ? { key, label: groupName(q.group), color: GROUP_COLOR[q.group], idx: [], total: GROUPS[q.group].length } : q.t === "rail" ? { key, label: "Jernbaner", color: "#475569", idx: [], total: 4 } : { key, label: "Forsyning", color: "#0e7490", idx: [], total: 2 };
      groups.push(g);
    }
    g.idx.push(i);
  }
  return (
    <div className="m3-props">
      {groups.map((g) => (
        <section key={g.key} className="m3-pgroup" aria-label={g.label}>
          <h4 style={{ "--c": g.color }}><i aria-hidden="true" />{g.label}<small>{g.idx.length}/{g.total}{g.idx.length === g.total ? " · komplet" : ""}</small></h4>
          {g.idx.map((i) => {
            const q = SQUARES[i]!;
            const h = b.houses[i]!;
            return (
              <div key={i} className={`m3-prop ${b.mort[i] ? "mort" : ""} ${selected === i ? "sel" : ""}`}>
                <button type="button" className="m3-pname" onClick={() => onSelect(i)} aria-pressed={selected === i}>
                  <span>{q.name}{b.mort[i] ? " (pantsat)" : ""}</span>
                  {h > 0 && <em aria-label={h === 5 ? "hotel" : `${h} huse`}>{h === 5 ? "🏨" : "🏠".repeat(h)}</em>}
                </button>
                {canManage && (
                  <span className="m3-pbtns">
                    {mgmt.build.includes(i) && <button className="btn small" disabled={busy} onClick={() => go({ type: "build", sq: i })} title={`Byg for ${(q as any).house} kr.`}><LuHammer size={14} aria-hidden="true" /> Byg</button>}
                    {mgmt.sell.includes(i) && <button className="btn ghost small" disabled={busy} onClick={() => go({ type: "sell", sq: i })}>Sælg hus</button>}
                    {mgmt.mortgage.includes(i) && <button className="btn ghost small" disabled={busy} onClick={() => go({ type: "mortgage", sq: i })} title={`Få ${mortgageValue(i)} kr.`}>Pantsæt</button>}
                    {mgmt.unmortgage.includes(i) && <button className="btn ghost small" disabled={busy} onClick={() => go({ type: "unmortgage", sq: i })} title={`Koster ${unmortgageCost(i)} kr.`}>Indløs</button>}
                  </span>
                )}
              </div>
            );
          })}
        </section>
      ))}
      {b.buildFailed && b.actor === you && <small className="muted">Du svarede forkert, så der bygges ikke mere i denne tur.</small>}
    </div>
  );
}

/* ───────────────────────── action bar ───────────────────────── */

export function TradeSummary({ t, names }: { t: any; names: (s: number) => string }) {
  const list = (a: number[]) => (a.length ? a.map((i) => SQUARES[i]!.name).join(", ") : "ingen ejendomme");
  return (
    <div className="m3-trade" role="status">
      <strong>{names(t.from)}</strong> foreslår en handel med <strong>{names(t.to)}</strong>:<br />
      {names(t.from)} giver: {list(t.giveProps)}{t.giveCash ? ` + ${kr(t.giveCash)}` : ""}<br />
      {names(t.to)} giver: {list(t.getProps)}{t.getCash ? ` + ${kr(t.getCash)}` : ""}
    </div>
  );
}

export function ActionBar({ b, you, over, hasQuestion, busy, held, go, names, onTrade }: {
  b: MonopolyView; you: number; over: boolean; hasQuestion: boolean; busy: boolean; held: boolean; go: (a: any) => void; names: (s: number) => string; onTrade: () => void;
}) {
  const me = b.players[you]!;
  const myTurn = b.actor === you && !over;
  const pos = me.pos;
  const [bidAmt, setBidAmt] = useState(0);
  useEffect(() => { if (b.auction) setBidAmt(b.auction.bid + (b.auction.bid < 100 ? 10 : 20)); }, [b.auction?.bid, b.auction?.sq]);
  const off = busy || held;
  if (over || hasQuestion) return null;
  const others = b.players.filter((x, s) => s !== you && !x.out).length;
  const tradeBtn = (
    <button className="btn small violet" disabled={off || others < 1} onClick={onTrade}><LuArrowRightLeft size={15} aria-hidden="true" /> Byt ejendomme</button>
  );
  if (!myTurn) {
    if (b.stage === "trade" && b.trade) return <div className="m3-bar"><TradeSummary t={b.trade} names={names} /></div>;
    if (b.stage === "auction" && b.auction) {
      return (
        <div className="m3-bar passive" role="status">
          <LuGavel size={18} aria-hidden="true" />
          <span>Auktion over <strong>{SQUARES[b.auction.sq]!.name}</strong>: højeste bud {b.auction.bidder === null ? "ingen" : `${kr(b.auction.bid)} (${names(b.auction.bidder)})`}. {names(b.auction.cur)} byder…</span>
        </div>
      );
    }
    return null;
  }
  return (
    <div className="m3-bar">
      {b.stage === "roll" && me.jail > 0 && !b.rolled && (
        <>
          <span className="m3-bar-note">Du sidder i fængsel (forsøg {Math.min(me.jail, 3)} af 3)</span>
          <button className="btn lime" disabled={off || b.jailQuiz} onClick={() => go({ type: "jailquiz" })}><LuKeyRound size={16} aria-hidden="true" /> Svar for at slippe ud</button>
          <button className="btn" disabled={off || me.cash < 50} onClick={() => go({ type: "payjail" })}>Betal 50 kr.</button>
          {me.jailCards > 0 && <button className="btn" disabled={off} onClick={() => go({ type: "usecard" })}><LuTicket size={16} aria-hidden="true" /> Løsladelseskort</button>}
        </>
      )}
      {b.stage === "roll" && !b.rolled && <button className="btn cta m3-roll" disabled={off} onClick={() => go({ type: "roll" })}><LuDices size={26} aria-hidden="true" /> {me.jail > 0 ? "Slå to ens" : "Slå med terningerne"}</button>}
      {b.stage === "buy" && (
        <>
          <button className="btn cta m3-roll" disabled={off || me.cash < priceOf(pos)} onClick={() => go({ type: "buy" })}><LuLandmark size={24} aria-hidden="true" /> Køb for {kr(priceOf(pos))}</button>
          <button className="btn ghost" disabled={off} onClick={() => go({ type: "decline" })}>Nej tak – auktion</button>
          {me.cash < priceOf(pos) && <small className="m3-bar-note">Du har ikke penge nok. Pantsæt noget, eller lad den gå på auktion.</small>}
        </>
      )}
      {b.stage === "manage" && <button className="btn cta m3-roll" disabled={off} onClick={() => go({ type: "end" })}>{b.doubles > 0 && me.jail === 0 ? <><LuDices size={26} aria-hidden="true" /> Slå igen – to ens!</> : <><LuCheck size={26} aria-hidden="true" /> Afslut turen</>}</button>}
      {b.stage === "debt" && b.debt && (
        <>
          <span className="m3-bar-note warn"><strong>Du skylder {kr(b.debt.total)}</strong> – du har {kr(me.cash)}. Sælg eller pantsæt, eller gå konkurs.</span>
          <button className="btn cta" disabled={off || me.cash < b.debt.total} onClick={() => go({ type: "paydebt" })}>Betal {kr(b.debt.total)}</button>
          <button className="btn ghost" disabled={off} onClick={() => go({ type: "bankrupt" })}>Gå konkurs</button>
        </>
      )}
      {b.stage === "auction" && b.auction && (
        <span className="m3-bidrow">
          <input type="number" aria-label="Dit bud" min={b.auction.bid + 1} max={me.cash} value={bidAmt} onChange={(e: any) => setBidAmt(Number(e.target.value))} />
          {[10, 50, 100].map((n) => <button key={n} type="button" className="btn ghost small" onClick={() => setBidAmt(b.auction!.bid + n)}>+{n}</button>)}
          <button className="btn cta" disabled={off || bidAmt <= b.auction.bid || bidAmt > me.cash} onClick={() => go({ type: "bid", amount: bidAmt })}><LuGavel size={18} aria-hidden="true" /> Byd {kr(bidAmt)}</button>
          <button className="btn ghost" disabled={off || b.auction.bidder === you} onClick={() => go({ type: "pass" })}>Pas</button>
        </span>
      )}
      {b.stage === "trade" && b.trade && b.trade.to === you && (
        <>
          <TradeSummary t={b.trade} names={names} />
          <button className="btn cta" disabled={off} onClick={() => go({ type: "accept" })}><LuCheck size={18} aria-hidden="true" /> Acceptér</button>
          <button className="btn ghost" disabled={off} onClick={() => go({ type: "reject" })}><LuX size={18} aria-hidden="true" /> Afslå</button>
        </>
      )}
      {(b.stage === "roll" || b.stage === "manage") && tradeBtn}
    </div>
  );
}

/* ───────────────────────── manage modes ───────────────────────── */

export type Mode = "build" | "sell" | "mortgage" | "unmortgage";
export const MODE_COLOR: Record<Mode, string> = { build: "#22e06b", sell: "#ff9a3c", mortgage: "#ff5c8a", unmortgage: "#38c8f5" };
export const MODE_LABEL: Record<Mode, string> = { build: "Byg", sell: "Sælg hus", mortgage: "Pantsæt", unmortgage: "Indløs" };
const MODE_HELP: Record<Mode, string> = {
  build: "Klik på et lysende felt for at bygge et hus (du skal svare på et spørgsmål).",
  sell: "Klik på et lysende felt for at sælge en bygning (du får halv pris).",
  mortgage: "Klik på et lysende felt for at pantsætte det (du får halvdelen af prisen).",
  unmortgage: "Klik på et lysende felt for at indløse det igen (koster 10 % ekstra).",
};
export const modeHelp = (m: Mode) => MODE_HELP[m];

/** the Byg / Sælg / Pantsæt / Indløs buttons – a mode lights up the fields it can be used on, a click on one of them does it */
export function ModeBar({ b, mode, onMode, disabled }: { b: MonopolyView; mode: Mode | null; onMode: (m: Mode | null) => void; disabled: boolean }) {
  const icon = { build: LuHammer, sell: LuHouse, mortgage: LuBanknote, unmortgage: LuLockOpen } as const;
  return (
    <div className="m3-modes" role="group" aria-label="Byg, sælg og pantsæt">
      {(Object.keys(MODE_LABEL) as Mode[]).map((k) => {
        const n = b.options[k].length, I = icon[k];
        return (
          <button key={k} type="button" className={`m3-mode ${mode === k ? "on" : ""}`} style={{ "--c": MODE_COLOR[k] }} disabled={disabled || n === 0} aria-pressed={mode === k}
            onClick={() => onMode(mode === k ? null : k)} title={n === 0 ? `${MODE_LABEL[k]}: ikke muligt lige nu` : MODE_HELP[k]}>
            <I size={18} aria-hidden="true" /><span>{MODE_LABEL[k]}</span>{n > 0 && <i>{n}</i>}
          </button>
        );
      })}
    </div>
  );
}
